import { createContext, useContext, useState, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../Firestore/FirestoreConfig";
import { doc, getDoc } from "firebase/firestore";
import db from "../Firestore/FirestoreConfig";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [justCompletedOnboarding, setJustCompletedOnboarding] = useState(false);
  const [hasCompletedOnboardingOnce, setHasCompletedOnboardingOnce] =
    useState(false);

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(
        "[AuthContext] Firebase auth state changed:",
        firebaseUser?.email
      );

      // Set loading to true whenever auth state changes (including login after logout)
      setLoading(true);

      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
        });

        // Check if coach document exists (skip if user just completed onboarding in this session)
        if (!hasCompletedOnboardingOnce) {
          try {
            const coachDocRef = doc(db, "coaches", firebaseUser.uid);
            const coachSnapshot = await getDoc(coachDocRef);

            if (!coachSnapshot.exists()) {
              console.log(
                "[AuthContext] Coach document not found, needs onboarding"
              );
              setNeedsOnboarding(true);
            } else {
              setNeedsOnboarding(false);
            }
          } catch (error) {
            console.error(
              "[AuthContext] Error checking coach document:",
              error
            );
            setNeedsOnboarding(false);
          }
        }
      } else {
        setUser(null);
        setNeedsOnboarding(false);
        setJustCompletedOnboarding(false);
        setHasCompletedOnboardingOnce(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [hasCompletedOnboardingOnce]);

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setNeedsOnboarding(false);
      setJustCompletedOnboarding(false);
      setHasCompletedOnboardingOnce(false);
      console.log("[AuthContext] User logged out");
    } catch (error) {
      console.error("[AuthContext] Logout error:", error);
      throw error;
    }
  };

  const completeOnboarding = () => {
    console.log("[AuthContext] Marking onboarding as completed");
    setNeedsOnboarding(false);
    setJustCompletedOnboarding(true);
    setHasCompletedOnboardingOnce(true);
  };

  const clearOnboardingFlag = () => {
    console.log("[AuthContext] Clearing onboarding completion flag");
    setJustCompletedOnboarding(false);
  };

  const value = {
    user,
    loading,
    logout,
    isAuthenticated: !!user,
    needsOnboarding,
    completeOnboarding,
    justCompletedOnboarding,
    clearOnboardingFlag,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
